/**
 * NowPlaying — Web component showing current track info and upcoming tracks
 */
import {
  Box,
  Text,
  Heading,
  VStack,
  HStack,
  Image,
  List,
  ListItem,
  Spinner,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { NowPlayingDto } from '../api/queueApi';

interface NowPlayingProps {
  nowPlaying: NowPlayingDto | null;
  isLoading?: boolean;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function NowPlaying({ nowPlaying, isLoading }: NowPlayingProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (!nowPlaying?.currentTrack) {
    return (
      <Box textAlign="center" py={4} color="gray.500">
        <Text>{t('player.noTrack')}</Text>
      </Box>
    );
  }

  const { currentTrack, trackProgress, scenarioName, playlistName, upcomingTracks } = nowPlaying;

  return (
    <VStack align="stretch" spacing={4} data-testid="now-playing">
      {/* Current Track */}
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>
          {t('player.nowPlaying')}
        </Text>
        <HStack spacing={3}>
          {currentTrack.thumbnailUrl ? (
            <Image
              src={currentTrack.thumbnailUrl}
              boxSize="48px"
              borderRadius="md"
              objectFit="cover"
              alt={currentTrack.title}
            />
          ) : (
            <Box
              boxSize="48px"
              borderRadius="md"
              bg="gray.200"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xl"
            >
              🎵
            </Box>
          )}
          <VStack align="start" spacing={0} flex={1}>
            <Heading size="sm" noOfLines={1}>
              {currentTrack.title}
            </Heading>
            {currentTrack.artist && (
              <Text fontSize="sm" color="gray.600" noOfLines={1}>
                {currentTrack.artist}
              </Text>
            )}
            {scenarioName && (
              <Text fontSize="xs" color="gray.400">
                {scenarioName}
                {playlistName && ` · ${playlistName}`}
              </Text>
            )}
          </VStack>
        </HStack>

        {/* Progress bar */}
        {trackProgress && (
          <HStack mt={2} spacing={2} fontSize="xs" color="gray.500">
            <Text>{formatTime(trackProgress.elapsedSeconds)}</Text>
            <Box flex={1} h="4px" bg="gray.200" borderRadius="full" overflow="hidden">
              <Box
                h="100%"
                bg="blue.500"
                borderRadius="full"
                w={`${(trackProgress.elapsedSeconds / Math.max(trackProgress.totalSeconds, 1)) * 100}%`}
              />
            </Box>
            <Text>{formatTime(trackProgress.totalSeconds)}</Text>
          </HStack>
        )}
      </Box>

      {/* Upcoming Tracks */}
      {upcomingTracks && upcomingTracks.length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={2}>
            {t('player.upNext')}
          </Text>
          <List spacing={1}>
            {upcomingTracks.map((track) => (
              <ListItem key={track.id}>
                <HStack justify="space-between" px={2} py={1} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" noOfLines={1}>
                      {track.title}
                    </Text>
                    {track.artist && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {track.artist}
                      </Text>
                    )}
                  </VStack>
                  <Text fontSize="xs" color="gray.400">
                    {formatTime(track.durationSeconds)}
                  </Text>
                </HStack>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </VStack>
  );
}
